/**
 * Locales for the PUBLIC surface (landing, FAQ, tutorials, legal). The signed-in console stays
 * English — it is an operator tool.
 *
 * ⚠️ THE PATH PREFIX IS `/ht`, NOT `/kr`. The owner asked for "kreyòl" and `/kr` is the tempting
 * abbreviation, but `kr` is the ISO 3166 country code for SOUTH KOREA. Search engines read a path
 * prefix as a locale hint and `hreflang` requires a real BCP-47 tag, so `/kr` would announce Korean
 * content in Haitian Creole. `ht` is the ISO 639-1 code for Haitian Creole and is correct in both
 * places.
 *
 * ⚠️ ENGLISH IS UNPREFIXED, deliberately. Moving it to `/en/...` would break every URL already
 * published: the sitemap, the JSON-LD `@id`s on each FAQ answer, the `Source:` lines in
 * /llms.txt, and any citation an answer engine has already made. English keeps `/faq`; Haitian
 * Creole gets `/ht/faq`; `hreflang` ties them together and `x-default` points at English.
 */

export const LOCALES = ["en", "ht"] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

/** Shown in the language switcher. Endonyms — a speaker looks for their own language's name. */
export const LOCALE_LABEL: Record<Locale, string> = {
  en: "English",
  ht: "Kreyòl",
};

/** `lang` attribute and hreflang value. Identical here, but kept separate from the URL prefix
 *  because they are different things and conflating them is what produces `/kr` + `hreflang="kr"`. */
export const LOCALE_TAG: Record<Locale, string> = {
  en: "en",
  ht: "ht",
};

/** Prefix a path for a locale. The default locale is unprefixed — see the note above. */
export const localePath = (locale: Locale, path: string): string => {
  const clean = path.startsWith("/") ? path : `/${path}`;
  if (locale === DEFAULT_LOCALE) return clean;
  return clean === "/" ? `/${locale}` : `/${locale}${clean}`;
};

/**
 * `alternates` for a page's Metadata: canonical for THIS locale plus every language alternate.
 * Both directions matter — a page that lists alternates but is not listed BY them is treated as a
 * one-way hint and commonly ignored, so every locale's page emits the full set.
 */
export const alternatesFor = (
  locale: Locale,
  path: string,
  siteUrl: string,
): {
  canonical: string;
  languages: Record<string, string>;
} => ({
  canonical: `${siteUrl}${localePath(locale, path)}`,
  languages: {
    ...Object.fromEntries(
      LOCALES.map((l) => [LOCALE_TAG[l], `${siteUrl}${localePath(l, path)}`]),
    ),
    "x-default": `${siteUrl}${localePath(DEFAULT_LOCALE, path)}`,
  },
});

/**
 * Home URL for a locale.
 *
 * It briefly returned the English root for every locale, because the landing page was not
 * translated and `localePath(locale, "/")` asserted a 404 in three places at once — the sitemap,
 * the Organization JSON-LD `url` and the BreadcrumbList's first item. Structured data naming a
 * missing page is worse than a broken link: a crawler reads it as a claim about the site.
 *
 * app/ht/page.tsx exists as of 2026-08-15, so it is honest again. Kept as its own function rather
 * than inlined: the next locale added will not have a landing page on day one either, and this is
 * where that gets handled.
 */
export const localeHome = (locale: Locale): string => localePath(locale, "/");
